import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import type { BlogCardPost, BlogCategoryOption } from "@Pages/Blog/BlogHomeRevamp/types";
import { ALL_BLOGS_SCOPE, LATEST_TAB_ID, getListingKey } from "@Pages/Blog/BlogHomeRevamp/settings";

export type BlogListingStatus = "request" | "success" | "failure";

export type BlogListing = {
  posts: BlogCardPost[];
  page: number;
  hasNextPage: boolean;
  status: BlogListingStatus;
};

export type AllBlogsPageSettings = {
  heading?: string | null;
  description?: string | null;
};

interface BlogAllPostsData {
  activeKeys: Record<string, string>;
  listings: Record<string, BlogListing>;
  categories: BlogCategoryOption[] | null;
  pageSettings: AllBlogsPageSettings | null;
  pageSettingsLoaded: boolean;
}

export const initialState: BlogAllPostsData = {
  activeKeys: { [ALL_BLOGS_SCOPE]: getListingKey(ALL_BLOGS_SCOPE, LATEST_TAB_ID) },
  listings: {},
  categories: null,
  pageSettings: null,
  pageSettingsLoaded: false,
};

const EMPTY_LISTING: BlogListing = { posts: [], page: 0, hasNextPage: false, status: "request" };

const blogAllPostsSlice = createSlice({
  name: "blogAllPostsData",
  initialState,
  reducers: {
    setActiveListing: (
      state: BlogAllPostsData,
      { payload }: PayloadAction<{ scope: string; key: string }>,
    ) => {
      state.activeKeys[payload.scope] = payload.key;
    },
    listingPageRequest: (state: BlogAllPostsData, { payload }: PayloadAction<string>) => {
      state.listings[payload] = {
        ...(state.listings[payload] ?? EMPTY_LISTING),
        status: "request",
      };
    },
    listingPageSuccess: (
      state: BlogAllPostsData,
      {
        payload,
      }: PayloadAction<{
        key: string;
        posts: BlogCardPost[];
        page: number;
        hasNextPage: boolean;
      }>,
    ) => {
      const previous = payload.page > 1 ? state.listings[payload.key]?.posts ?? [] : [];
      const seen = new Set(previous.map(({ id }) => id));
      state.listings[payload.key] = {
        posts: [...previous, ...payload.posts.filter(({ id }) => !seen.has(id))],
        page: payload.page,
        hasNextPage: payload.hasNextPage,
        status: "success",
      };
    },
    listingPageFailure: (state: BlogAllPostsData, { payload }: PayloadAction<string>) => {
      state.listings[payload] = {
        ...(state.listings[payload] ?? EMPTY_LISTING),
        status: "failure",
      };
    },
    setCategories: (state: BlogAllPostsData, { payload }: PayloadAction<BlogCategoryOption[]>) => {
      state.categories = payload;
    },
    pageSettingsLoaded: (
      state: BlogAllPostsData,
      { payload }: PayloadAction<AllBlogsPageSettings | null>,
    ) => {
      state.pageSettings = payload;
      state.pageSettingsLoaded = true;
    },
  },
});

export const {
  setActiveListing,
  listingPageRequest,
  listingPageSuccess,
  listingPageFailure,
  setCategories,
  pageSettingsLoaded,
} = blogAllPostsSlice.actions;

export default blogAllPostsSlice.reducer;
